import React, { useEffect, useState } from 'react';
import analyticsService from '../../services/analyticsService';
import EmptyState from './EmptyState';
import type { AnalyticsFilters, TopTrade } from '../../types/analytics';

const fmtInr = (n: number) => `${n < 0 ? '-' : ''}₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;

const TradeRow: React.FC<{ t: TopTrade; positive: boolean }> = ({ t, positive }) => (
  <tr className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700">
    <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-200 truncate" title={t.symbol}>{t.symbol}</td>
    <td className={`px-3 py-2 text-xs font-medium ${t.action === 'BUY' ? 'text-green-600' : 'text-red-500'}`}>{t.action}</td>
    <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{t.exitType}</td>
    <td className={`px-3 py-2 text-xs text-right tabular-nums font-semibold ${positive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
      {fmtInr(t.pnl)}
    </td>
  </tr>
);

const Table: React.FC<{ trades: TopTrade[]; positive: boolean }> = ({ trades, positive }) => {
  if (!trades.length) return <EmptyState message="No trades." />;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr className="border-y border-gray-200 dark:border-gray-700">
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Symbol</th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Side</th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Exit</th>
            <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">P&amp;L</th>
          </tr>
        </thead>
        <tbody>
          {trades.map(t => <TradeRow key={t.positionId} t={t} positive={positive} />)}
        </tbody>
      </table>
    </div>
  );
};

const TopTradesTable: React.FC<{ filters: AnalyticsFilters }> = ({ filters }) => {
  const [winners, setWinners] = useState<TopTrade[]>([]);
  const [losers, setLosers] = useState<TopTrade[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      analyticsService.getTopTrades(filters, 'winners', 10),
      analyticsService.getTopTrades(filters, 'losers', 10),
    ]).then(([w, l]) => {
      if (!cancelled) {
        setWinners(w.items);
        setLosers(l.items);
      }
    }).catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [filters]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Top 10 winners</div>
        <Table trades={winners} positive={true} />
      </div>
      <div>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Top 10 losers</div>
        <Table trades={losers} positive={false} />
      </div>
    </div>
  );
};

export default TopTradesTable;
